import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './SearchVisualizers.module.css';

const generateUniqueSortedArray = (size: number, maxVal: number): number[] => {
  if (size > maxVal) {
    console.warn("Array size exceeds max unique values. Some values might be repeated.");
  }

  const uniqueNumbers = new Set<number>();
  while (uniqueNumbers.size < size) {
    uniqueNumbers.add(Math.floor(Math.random() * maxVal) + 1);
  }
  const arr = Array.from(uniqueNumbers).sort((a, b) => a - b);
  return arr;
};


const LINEAR_SEARCH_CODE = `function linearSearch(arr, target) {
  for (let i = 0; i < arr.length; i++) {
    if (arr[i] === target) {
      return i;
    }
  }
  return -1;
}`;

const BINARY_SEARCH_CODE = `function binarySearch(arr, target) {
  let left = 0;
  let right = arr.length - 1;

  while (left <= right) {
    const mid = Math.floor((left + right) / 2);
    if (arr[mid] === target) {
      return mid;
    } else if (arr[mid] < target) {
      left = mid + 1;
    } else {
      right = mid - 1;
    }
  }
  return -1;
}`;

const CompareSearches: React.FC = () => {
  const navigate = useNavigate();
  const [array, setArray] = useState<number[]>([]);
  const [target, setTarget] = useState<number>(-1);
  const [linearTime, setLinearTime] = useState<number | null>(null);
  const [binaryTime, setBinaryTime] = useState<number | null>(null);
  const [linearHighlightedLine, setLinearHighlightedLine] = useState<number>(-1);
  const [binaryHighlightedLine, setBinaryHighlightedLine] = useState<number>(-1);
  const [linearHighlightedIndex, setLinearHighlightedIndex] = useState<number>(-1);
  const [binaryHighlightedIndices, setBinaryHighlightedIndices] = useState<number[]>([]);
  const linearTimerRef = useRef<number | null>(null);
  const binaryTimerRef = useRef<number | null>(null);

  useEffect(() => {
    handleRandomize();
  }, []);

  const handleRandomize = () => {
    handleStop();
    const arraySize = Math.floor(Math.random() * (20 - 10 + 1)) + 10; 
    const newArray = generateUniqueSortedArray(arraySize, 100); 
    setArray(newArray);
    
    const randomTargetIndex = Math.floor(Math.random() * newArray.length);
    setTarget(newArray[randomTargetIndex]);
  };

  const handleStop = () => {
    if (linearTimerRef.current) clearInterval(linearTimerRef.current);
    if (binaryTimerRef.current) clearInterval(binaryTimerRef.current);
    setLinearTime(null);
    setBinaryTime(null);
    setLinearHighlightedLine(-1);
    setBinaryHighlightedLine(-1);
    setLinearHighlightedIndex(-1);
    setBinaryHighlightedIndices([]);
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const runComparison = () => {
    handleStop(); 
      let linearCurrentIndex = 0;
    linearTimerRef.current = window.setInterval(() => {
      setLinearTime(prev => (prev !== null ? prev + 1 : 1));
      setLinearHighlightedLine(2);
      setLinearHighlightedIndex(linearCurrentIndex); 

      if (linearCurrentIndex < array.length) {
        if (array[linearCurrentIndex] === target) {
          setLinearHighlightedLine(4);
          clearInterval(linearTimerRef.current!);
        }
      } else {
        setLinearHighlightedLine(5); 
        clearInterval(linearTimerRef.current!);
      }
      linearCurrentIndex++;
    }, 1000);

    let left = 0;
    let right = array.length - 1;
    let binaryFound = false;

    binaryTimerRef.current = window.setInterval(() => {
      if (binaryFound || left > right) {
        if (!binaryFound) {
          setBinaryHighlightedLine(11); 
          setBinaryHighlightedIndices([]); 
        }
        clearInterval(binaryTimerRef.current!);
        return;
      }

      setBinaryTime(prev => (prev !== null ? prev + 1 : 1)); 
      setBinaryHighlightedLine(5);

      const mid = Math.floor((left + right) / 2);
      setBinaryHighlightedIndices([mid, left, right]); 
      if (array[mid] === target) {
        setBinaryHighlightedLine(6); 
        setBinaryHighlightedIndices([mid]); 
        binaryFound = true;
        clearInterval(binaryTimerRef.current!);
      } else if (array[mid] < target) {
        setBinaryHighlightedLine(8); 
        left = mid + 1;
      } else {
        setBinaryHighlightedLine(10); 
        right = mid - 1;
      }
    }, 1000);
  };

  return (
    <div className={styles.container}>
      <button onClick={handleGoBack} className={styles.returnButton}>
        &larr; Return
      </button>
      <h1>Compare Search Algorithms</h1>
      <p className={styles.description}>
        Imagine you're searching for a specific book on a library shelf.
        If the books are not in any order, you have to look at each one, one by one.
        However, if the books are sorted by title, you can open the shelf to the middle and immediately eliminate half of the books.
      </p>
      <p className={styles.disclaimer}>
        *Note: The timers are set to execute per second for demonstration purposes only.
      </p>
      <div className={styles.arrayDisplay}>
        <p>Current Array ({array.length} elements) | Target Value: {target !== -1 ? target : '...'}</p>
        <div className={styles.barContainer}>
          {array.map((value, index) => (
            <div 
              key={index} 
              className={`${styles.bar} 
                          ${linearHighlightedIndex === index ? styles.linearHighlight : ''} 
                          ${binaryHighlightedIndices.includes(index) ? styles.binaryHighlight : ''}
                          ${(linearTime !== null && array[linearHighlightedIndex] === target && linearHighlightedIndex === index) ? styles.linearFound : ''}
                          ${(binaryTime !== null && binaryHighlightedIndices.length === 1 && binaryHighlightedIndices[0] === index) ? styles.binaryFound : ''}
                          `}
            >
              {value}
            </div>
          ))}
        </div>
      </div>
      <div className={styles.controls}>
        <button onClick={handleRandomize}>Randomize Array</button>
        <button onClick={runComparison}>Run Comparison</button>
        <button onClick={handleStop}>Stop</button>
      </div>
      <div className={styles.comparevisualization}>
        <div className={styles.algorithmCard}>
          <h2>Linear Search</h2>
          <p>
            Time taken: <b>{linearTime !== null ? `${linearTime}s` : '...'}</b>
          </p>
          <div className={styles.codeDisplay}>
            {LINEAR_SEARCH_CODE.split('\n').map((line, index) => (
              <div key={index} className={`${styles.codeLine} ${linearHighlightedLine === index + 1 ? styles.linearHighlightedLine : ''}`}>
                {line}
              </div>
            ))}
          </div>
        </div>
        <div className={styles.algorithmCard}>
          <h2>Binary Search</h2>
          <p>
            Time taken: <b>{binaryTime !== null ? `${binaryTime}s` : '...'}</b>
          </p>
          <div className={styles.codeDisplay}>
            {BINARY_SEARCH_CODE.split('\n').map((line, index) => (
              <div key={index} className={`${styles.codeLine} ${binaryHighlightedLine === index + 1 ? styles.binaryHighlightedLine : ''}`}>
                {line}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompareSearches;